import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CourseMaterial } from '../interfaces/course-material';
import { ApiCollectionResponse, ApiResponse } from '../interfaces/responses/api-response';

/**
 * Endpoints exposed to the authenticated student for accessing their
 * digital library (course materials shared by professors on their learning
 * path or in their groups).
 *
 * - GET /api/students/me/course-materials              → list
 * - GET /api/students/me/course-materials/{id}/download → binary file
 */
@Injectable({ providedIn: 'root' })
export class StudentCourseMaterialService {
  private readonly base = `${environment.BASE_URL}/students/me/course-materials`;

  constructor(private http: HttpClient) {}

  /** All materials the current student has access to. */
  getMyMaterials(): Observable<ApiCollectionResponse<CourseMaterial>> {
    return this.http.get<ApiCollectionResponse<CourseMaterial>>(this.base);
  }

  /**
   * Download a material as a Blob — caller is responsible for triggering the
   * browser save (createObjectURL + anchor click). Returns the full HttpResponse
   * so the caller can read the `Content-Disposition` header for the filename.
   */
  download(id: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.base}/${id}/download`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  /**
   * Mes supports favoris.
   *
   * L'API n'expose plus de route dédiée : `GET /students/me/course-materials`
   * renseigne déjà `isFavorite`, on filtre donc localement. `FavoritesComponent`
   * appelait cette méthode sans qu'elle existe — le fichier n'étant référencé
   * par aucune route, rien ne le compilait et l'incohérence passait inaperçue.
   *
   * Deux différences assumées avec l'ancien `GET .../favorites` : le tri est
   * celui de la bibliothèque (par date de publication) et non l'ordre de mise
   * en favori, que le DTO ne transporte pas ; et un support devenu inaccessible
   * — groupe quitté — disparaît de la liste au lieu d'y rester épinglé.
   */
  getFavorites(): Observable<ApiCollectionResponse<CourseMaterial>> {
    return this.getMyMaterials().pipe(
      map((res) => {
        const data = (res?.data ?? []).filter((m) => m.isFavorite);
        return { ...res, data, count: data.length };
      }),
    );
  }

  /** Add a material to favorites. */
  addFavorite(id: number): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(`${this.base}/${id}/favorite`, {});
  }

  /** Remove a material from favorites. */
  removeFavorite(id: number): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.base}/${id}/favorite`);
  }
}
